/**
 * Git API Bridge
 *
 * Seam: delete these normalizers when store/managers consume generated Nest git types directly.
 */

// ============================================================================
// Type Definitions
// ============================================================================

export interface GitTarget {
    workspaceId: string
    workspaceRoot: string
    directory?: string
    worktreeId?: string
}

interface IsGitInstalledResponse {
    installed: boolean
    version?: string
}

export interface IsGitDirectoryParams {
    directory: string
}

export type IsGitDirectoryResponse =
    | {
          isGitDirectory: true
          repoRoot: string
          relativePath: string
          mainBranch: string
          hasGhCli: boolean
      }
    | {
          isGitDirectory: false
          error?: string
      }

export interface CheckGhCliResponse {
    hasGhCli: boolean
}

export type GitStatusParams = GitTarget

export interface UncommittedChangesStats {
    filesChanged: number
    binaryFiles: number
    insertions: number
    deletions: number
    unknownFiles?: number
}

/** File info returned from git status with binary detection */
export interface GitFileInfo {
    path: string
    binary: boolean
    status?: "added" | "deleted" | "modified" | "renamed"
    lineStats?: {
        insertions: number
        deletions: number
    }
}

export interface GitSummaryResponse {
    // Git ref info
    branch: string | null // Current branch name (null if detached HEAD)
    headCommit: string // Short SHA of HEAD commit

    // Remote tracking
    ahead: number | null // Commits ahead of upstream (null if no upstream)

    // Working tree status
    hasChanges: boolean
    staged: GitChangeSetResponse
    unstaged: GitChangeSetResponse
    untracked: GitChangeSetResponse
    total: UncommittedChangesStats
}

export interface GitChangeSetResponse {
    files: GitFileInfo[]
    stats: UncommittedChangesStats
}

type GitStatusChangeSet = GitChangeSetResponse & {
    patch: string
}

export type GitStatusResponse = Omit<GitSummaryResponse, "staged" | "unstaged"> & {
    staged: GitStatusChangeSet
    unstaged: GitStatusChangeSet
}

interface ListFilesParams extends GitTarget {
    query?: string
    limit?: number
}

interface ListFilesResponse {
    files: string[]
    truncated: boolean
}

interface ListBranchesParams extends GitTarget {
    includeRemote?: boolean
}

export interface BranchInfo {
    name: string
    isDefault: boolean
    isRemote: boolean
    sha?: string
}

interface ListBranchesResponse {
    branches: BranchInfo[]
    defaultBranch: string
    defaultBranchSha?: string
}

export interface ResolvePathParams {
    path: string
}

export interface ResolvePathResponse {
    resolvedPath: string
    exists: boolean
    isDirectory: boolean
}

export interface InitGitParams {
    directory: string
}

export interface InitGitResponse {
    success: boolean
    error?: string
}

interface GetChangedFilesParams extends GitTarget {
    fromTreeish: string
    toTreeish: string
}

export interface ChangedFileInfo {
    path: string
    status: "added" | "deleted" | "modified" | "renamed"
    oldPath?: string
    binary?: boolean
}

interface GetChangedFilesResponse {
    files: ChangedFileInfo[]
    fromTreeish: string
    toTreeish: string
}

interface GetFileAtTreeishParams extends GitTarget {
    treeish: string
    filePath: string
}

interface GetFileAtTreeishResponse {
    content: string
    exists: boolean
    tooLarge?: boolean
}

interface GetFilePairParams extends GitTarget {
    fromTreeish: string
    toTreeish: string
    filePath: string
    oldPath?: string
}

interface GetDifftasticParams extends GetFilePairParams {
    contextLines: number
}

export interface GetDifftasticResponse {
    available: boolean
    output: string
    error: string
    elapsedMs: number
    truncated: boolean
    hitTimeout: boolean
}

export interface GetFilePairResponse {
    before: string
    after: string
    tooLarge?: boolean
}

interface GetWorktreeFilePatchParams extends GitTarget {
    fromTreeish: string
    filePath: string
    oldPath?: string
    contextLines: 1 | 3 | 10 | 25 | 100
    allowTruncation?: boolean
}

interface GetCommitFilePatchParams extends GitTarget {
    commit: string
    filePath: string
    oldPath?: string
    contextLines: 1 | 3 | 10 | 25 | 100
    allowTruncation?: boolean
}

export interface GetFilePatchResponse {
    patch: string
    truncated: boolean
    heavy: boolean
    stats: {
        insertions: number
        deletions: number
        changedLines: number
        hunkCount: number
    }
}

interface GetGitLogParams extends GitTarget {
    ref?: string
    limit?: number
    skip?: number
}

export interface GitLogEntry {
    sha: string
    shortSha: string
    message: string
    author: string
    date: string
    relativeDate: string
    parentCount: number
}

interface GetGitLogResponse {
    commits: GitLogEntry[]
    hasMore: boolean
}

interface GetCommitCountParams extends GitTarget {
    from: string
    to?: string
}

interface GetCherryCommitCountParams extends GitTarget {
    upstream: string
    head: string
    limit: string
}

interface GetCommitFilesParams extends GitTarget {
    commit: string
}

interface GetCommitFilesResponse {
    files: ChangedFileInfo[]
}

// ============================================================================
// API Check
// ============================================================================

import { isNestApiAvailable, type NestRequestOptions, requestNestMethod } from "../nest/client"
import { type NestFileBytes, type NestGitFileBytesDescription, describeNestGitFileBytes, downloadNestGitFileBytes } from "../nest/fileBytes"
import type { ChangedFile, Commit, GitChangeSetSummary, GitChangeStats, GitSummary } from "../nest/generated"
import { isCodeModuleAvailable } from "./capabilities"
import { relativeFromWorkspaceRoot, trimTrailingSlash } from "./runtimeContext"

function requireNest(): void {
    if (!isNestApiAvailable()) throw new Error("Nest API is required")
}

type GitReadOptions = Pick<NestRequestOptions, "signal">

function normalizePath(value: string): string {
    return trimTrailingSlash(value.replace(/\\/g, "/"))
}

function joinPath(...parts: string[]): string {
    return parts
        .filter((part) => part.length > 0)
        .map((part, index) => (index === 0 ? normalizePath(part) : part.replace(/^\/+|\/+$/g, "")))
        .filter((part) => part.length > 0)
        .join("/")
}

function relativePathFromRoot(root: string, directory: string): string {
    const normalizedRoot = normalizePath(root)
    const normalizedDirectory = normalizePath(directory)
    if (normalizedRoot === normalizedDirectory) return ""
    const prefix = `${normalizedRoot}/`
    if (normalizedDirectory.startsWith(prefix)) return normalizedDirectory.slice(prefix.length)
    return ""
}

function requireNestGitTarget(params: GitTarget, operation: string): { workspaceId: string; path: string; worktreeId?: string } {
    requireNest()
    if (!params.workspaceRoot) {
        throw new Error(`workspaceRoot is required for Nest ${operation}`)
    }
    const directory = params.directory ?? params.workspaceRoot
    return {
        workspaceId: params.workspaceId,
        path: relativeFromWorkspaceRoot(params.workspaceRoot, directory),
        ...(params.worktreeId ? { worktreeId: params.worktreeId } : {}),
    }
}

function normalizeBranchName(branch?: string): string | null {
    if (!branch) return null
    const withoutTracking = branch.split("...")[0] ?? branch
    const withoutStatus = withoutTracking.split(" ")[0] ?? withoutTracking
    return withoutStatus && withoutStatus !== "HEAD" ? withoutStatus : null
}

function normalizeGitFileStatus(status?: string): GitFileInfo["status"] | undefined {
    switch (status) {
        case "added":
        case "deleted":
        case "modified":
        case "renamed":
            return status
        case "A":
            return "added"
        case "D":
            return "deleted"
        case "R":
            return "renamed"
        case "M":
            return "modified"
        default:
            return undefined
    }
}

function normalizeGitFileInfo(file: ChangedFile): GitFileInfo {
    const status = normalizeGitFileStatus(file.status)
    return {
        path: file.path,
        binary: file.binary ?? false,
        ...(status ? { status } : {}),
        ...(file.lineStats ? { lineStats: file.lineStats } : {}),
    }
}

function normalizeChangedFileInfo(file: ChangedFile): ChangedFileInfo {
    return {
        path: file.path,
        status: normalizeGitFileStatus(file.status) ?? "modified",
        ...(file.oldPath ? { oldPath: file.oldPath } : {}),
        ...(file.binary !== undefined ? { binary: file.binary } : {}),
    }
}

function formatRelativeDate(timestamp?: string): string {
    if (!timestamp) return ""
    const value = Date.parse(timestamp)
    if (Number.isNaN(value)) return timestamp
    const seconds = Math.max(0, Math.floor((Date.now() - value) / 1000))
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months}mo ago`
    return `${Math.floor(months / 12)}y ago`
}

function normalizeLogCommit(commit: Commit): GitLogEntry {
    return {
        sha: commit.sha,
        shortSha: commit.sha.slice(0, 7),
        message: commit.subject,
        author: commit.author ?? "",
        date: commit.timestamp ?? "",
        relativeDate: formatRelativeDate(commit.timestamp),
        parentCount: commit.parentCount,
    }
}

export async function getManagedWorktreeBasePath(): Promise<string> {
    const base = await requestNestMethod("host.path.mkdir", { path: "~/.wildlark/workspaces/worktrees" })
    return base.abs
}

export async function getManagedWorktreePath(id: string): Promise<string> {
    return joinPath(await getManagedWorktreeBasePath(), id)
}

function normalizeStats(stats: GitChangeStats): UncommittedChangesStats {
    return {
        filesChanged: stats.filesChanged,
        binaryFiles: stats.binaryFiles,
        insertions: stats.insertions,
        deletions: stats.deletions,
        unknownFiles: stats.unknownFiles ?? 0,
    }
}

function normalizeChangeSet(changeSet: GitChangeSetSummary): GitChangeSetResponse {
    return {
        files: (changeSet.files ?? []).map(normalizeGitFileInfo),
        stats: normalizeStats(changeSet.stats),
    }
}

function normalizeGitSummary(summary: GitSummary): GitSummaryResponse {
    return {
        branch: normalizeBranchName(summary.branch),
        headCommit: summary.headCommit ?? "",
        ahead: summary.ahead ?? null,
        hasChanges: !summary.clean || summary.total.filesChanged > 0,
        staged: normalizeChangeSet(summary.staged),
        unstaged: normalizeChangeSet(summary.unstaged),
        untracked: normalizeChangeSet(summary.untracked),
        total: normalizeStats(summary.total),
    }
}

// ============================================================================
// Git API Functions
// ============================================================================

/**
 * Check if git is installed on the system
 */
async function isGitInstalled(): Promise<IsGitInstalledResponse> {
    requireNest()
    const response = await requestNestMethod("git.installed.read")
    return { installed: response.installed, version: response.version }
}

/**
 * Check if a directory is within a git repository and get repo info
 */
export async function isGitDirectory(params: IsGitDirectoryParams): Promise<IsGitDirectoryResponse> {
    requireNest()
    const response = await requestNestMethod("git.repository.path.read", { path: params.directory })
    if (!response.isRepo || !response.root) {
        return { isGitDirectory: false }
    }

    const gh = await checkGhCli().catch(() => ({ hasGhCli: false }))
    return {
        isGitDirectory: true,
        repoRoot: response.root,
        relativePath: relativePathFromRoot(response.root, params.directory),
        mainBranch: normalizeBranchName(response.branch) ?? "main",
        hasGhCli: gh.hasGhCli,
    }
}

/**
 * Check if gh CLI is installed and authenticated (lightweight, no caching)
 */
export async function checkGhCli(): Promise<CheckGhCliResponse> {
    requireNest()
    const response = await requestNestMethod("git.gh.read")
    return { hasGhCli: response.installed }
}

/**
 * Get git status including current branch, HEAD commit, and working tree changes
 */
export async function getGitStatus(params: GitStatusParams): Promise<GitStatusResponse> {
    const pathParams = requireNestGitTarget(params, "git status")
    const response = await requestNestMethod("git.status.read", {
        ...pathParams,
        includePatch: true,
    })
    const summary = normalizeGitSummary(response.summary)
    return {
        ...summary,
        staged: { ...summary.staged, patch: response.stagedPatch ?? "" },
        unstaged: { ...summary.unstaged, patch: response.unstagedPatch ?? "" },
    }
}

/**
 * Get lightweight git summary for branch/head/status/file lists without patch payloads
 */
export async function getGitSummary(params: GitStatusParams): Promise<GitSummaryResponse> {
    const pathParams = requireNestGitTarget(params, "git summary")
    const response = await requestNestMethod("git.status.summary", {
        ...pathParams,
    })
    return normalizeGitSummary(response)
}

/**
 * List files in a repository or worktree with optional fuzzy search
 */
async function listFiles(params: ListFilesParams): Promise<ListFilesResponse> {
    const pathParams = requireNestGitTarget(params, "file listing")
    return requestNestMethod("git.file.list", {
        ...pathParams,
        query: params.query,
        limit: params.limit,
    })
}

/**
 * List branches in a repository
 */
async function listBranches(params: ListBranchesParams): Promise<ListBranchesResponse> {
    const pathParams = requireNestGitTarget(params, "branch listing")
    const response = await requestNestMethod("git.branch.list", {
        ...pathParams,
        includeRemote: params.includeRemote ?? false,
    })
    const currentBranch = response.branches.find((branch) => branch.current)
    const defaultBranch = response.defaultBranch ?? currentBranch?.name ?? response.branches.find((branch) => !branch.remote)?.name ?? "main"
    const defaultBranchSha = response.defaultBranch && response.defaultBranchSha ? response.defaultBranchSha : undefined
    return {
        branches: response.branches.map((branch) => ({
            name: branch.name,
            isDefault: branch.name === defaultBranch,
            isRemote: branch.remote,
            ...(branch.sha ? { sha: branch.sha } : {}),
        })),
        defaultBranch,
        ...(defaultBranchSha ? { defaultBranchSha } : {}),
    }
}

/**
 * Resolve a path, expanding ~ and environment variables
 */
export async function resolvePath(params: ResolvePathParams): Promise<ResolvePathResponse> {
    requireNest()
    const response = await requestNestMethod("host.path.resolve", params)
    return { resolvedPath: response.abs, exists: response.exists, isDirectory: response.isDir }
}

/**
 * Initialize a git repository with a default .gitignore
 */
export async function initGit(params: InitGitParams): Promise<InitGitResponse> {
    requireNest()
    const response = await requestNestMethod("git.repository.path.init", { path: params.directory })
    return { success: response.initialized, error: response.error }
}

/**
 * Check if Git API is available (running in Electron)
 */
export function isGitApiAvailable(): boolean {
    return isCodeModuleAvailable() && isNestApiAvailable()
}

/**
 * Get list of files changed between two treeishes
 */
async function getChangedFiles(params: GetChangedFilesParams): Promise<GetChangedFilesResponse> {
    const pathParams = requireNestGitTarget(params, "changed files")
    const response = await requestNestMethod("git.changed.files", {
        ...pathParams,
        fromTreeish: params.fromTreeish,
        toTreeish: params.toTreeish,
    })
    return {
        files: response.files.map(normalizeChangedFileInfo),
        fromTreeish: params.fromTreeish,
        toTreeish: params.toTreeish,
    }
}

/**
 * Get file content at a specific treeish
 */
async function getFileAtTreeish(params: GetFileAtTreeishParams, options: GitReadOptions = {}): Promise<GetFileAtTreeishResponse> {
    const pathParams = requireNestGitTarget(params, "file-at-tree read")
    const request = {
        ...pathParams,
        treeish: params.treeish,
        filePath: params.filePath,
    }
    const response = options.signal
        ? await requestNestMethod("git.file.atTree.read", request, { signal: options.signal })
        : await requestNestMethod("git.file.atTree.read", request)
    return {
        content: response.content,
        exists: response.exists,
        tooLarge: response.truncated,
    }
}

async function describeFileAtTreeishBytes(params: GetFileAtTreeishParams, options: { signal?: AbortSignal } = {}): Promise<NestGitFileBytesDescription> {
    const pathParams = requireNestGitTarget(params, "git file byte description")
    return describeNestGitFileBytes(
        {
            ...pathParams,
            treeish: params.treeish,
            filePath: params.filePath,
        },
        options
    )
}

async function readFileAtTreeishBytes(
    params: GetFileAtTreeishParams,
    options: { signal?: AbortSignal; range?: { start: number; endExclusive: number } } = {}
): Promise<NestFileBytes> {
    const pathParams = requireNestGitTarget(params, "git file byte read")
    return downloadNestGitFileBytes(
        {
            ...pathParams,
            treeish: params.treeish,
            filePath: params.filePath,
        },
        options
    )
}

/**
 * Get both before and after content for a file between two treeishes
 */
async function getFilePair(params: GetFilePairParams, options: GitReadOptions = {}): Promise<GetFilePairResponse> {
    const pathParams = requireNestGitTarget(params, "file-pair read")
    const request = {
        ...pathParams,
        fromTreeish: params.fromTreeish,
        toTreeish: params.toTreeish,
        filePath: params.filePath,
        oldPath: params.oldPath,
    }
    const response = options.signal
        ? await requestNestMethod("git.file.pair.read", request, { signal: options.signal })
        : await requestNestMethod("git.file.pair.read", request)
    return {
        before: response.old.content,
        after: response.new.content,
        tooLarge: response.old.truncated || response.new.truncated,
    }
}

async function getDifftastic(params: GetDifftasticParams, options: GitReadOptions = {}): Promise<GetDifftasticResponse> {
    const pathParams = requireNestGitTarget(params, "difftastic preview")
    const request = {
        ...pathParams,
        fromTreeish: params.fromTreeish,
        toTreeish: params.toTreeish,
        filePath: params.filePath,
        oldPath: params.oldPath,
        maxBytes: 1_048_576,
        contextLines: params.contextLines,
    }
    const response = options.signal
        ? await requestNestMethod("git.diff.difftastic.read", request, { signal: options.signal })
        : await requestNestMethod("git.diff.difftastic.read", request)
    return {
        available: response.available,
        output: response.output ?? "",
        error: response.error ?? "",
        elapsedMs: response.elapsedMs,
        truncated: response.truncated,
        hitTimeout: response.hitTimeout,
    }
}

async function getWorktreeFilePatch(params: GetWorktreeFilePatchParams, options: GitReadOptions = {}): Promise<GetFilePatchResponse> {
    const pathParams = requireNestGitTarget(params, "worktree file patch")
    const request = {
        ...pathParams,
        fromTreeish: params.fromTreeish,
        filePath: params.filePath,
        oldPath: params.oldPath,
        contextLines: params.contextLines,
    }
    return options.signal
        ? requestNestMethod("git.patch.worktreeFile.read", request, { signal: options.signal })
        : requestNestMethod("git.patch.worktreeFile.read", request)
}

async function getCommitFilePatch(params: GetCommitFilePatchParams, options: GitReadOptions = {}): Promise<GetFilePatchResponse> {
    const pathParams = requireNestGitTarget(params, "commit file patch")
    const request = {
        ...pathParams,
        commit: params.commit,
        filePath: params.filePath,
        oldPath: params.oldPath,
        contextLines: params.contextLines,
    }
    return options.signal
        ? requestNestMethod("git.patch.commitFile.read", request, { signal: options.signal })
        : requestNestMethod("git.patch.commitFile.read", request)
}

async function getLog(params: GetGitLogParams): Promise<GetGitLogResponse> {
    const pathParams = requireNestGitTarget(params, "git log")
    const response = await requestNestMethod("git.log.list", {
        ...pathParams,
        ref: params.ref,
        limit: params.limit,
        cursor: params.skip ? String(params.skip) : "",
    })
    return {
        commits: response.items.map(normalizeLogCommit),
        hasMore: response.hasMore,
    }
}

async function getCommitCount(params: GetCommitCountParams): Promise<number> {
    const pathParams = requireNestGitTarget(params, "commit count")
    const response = await requestNestMethod("git.commit.count", {
        ...pathParams,
        from: params.from,
        to: params.to ?? "HEAD",
    })
    return response.count
}

async function getCherryCommitCount(params: GetCherryCommitCountParams): Promise<{ available: boolean; count: number }> {
    const pathParams = requireNestGitTarget(params, "cherry commit count")
    return requestNestMethod("git.commit.cherryCount", {
        ...pathParams,
        upstream: params.upstream,
        head: params.head,
        limit: params.limit,
    })
}

async function getCommitFiles(params: GetCommitFilesParams): Promise<GetCommitFilesResponse> {
    const pathParams = requireNestGitTarget(params, "commit files")
    const response = await requestNestMethod("git.commit.files", {
        ...pathParams,
        commit: params.commit,
    })
    return {
        files: response.files.map(normalizeChangedFileInfo),
    }
}

/**
 * Git API namespace for convenient imports
 */
export const gitApi = {
    isGitInstalled,
    isGitDirectory,
    checkGhCli,
    getGitSummary,
    getGitStatus,
    listFiles,
    listBranches,
    resolvePath,
    initGit,
    getLog,
    getCommitCount,
    getCherryCommitCount,
    getCommitFiles,
    getChangedFiles,
    getFileAtTreeish,
    describeFileAtTreeishBytes,
    readFileAtTreeishBytes,
    getFilePair,
    getDifftastic,
    getWorktreeFilePatch,
    getCommitFilePatch,
    isAvailable: isGitApiAvailable,
}
